import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { cn } from '@/lib/utils'
import { Film, Image as ImageIcon, Music, Folder, ChevronRight, Check, Clock, HardDrive, Send, X, ChevronLeft, ChevronRight as ChevronRightIcon, Play, Pause, ArrowRight, Maximize2, Sparkles, FastForward, Upload, FileText } from 'lucide-react'
import { CaptionSampleBox, useCaptionPreviewFonts } from './CaptionStylePanel'
import { getVoiceName, getPresetVoicePreviewUrl } from '@/lib/voicePresets'

// --- Types ---

interface MediaItem {
  path: string
  name: string
  thumbnail_url?: string
  type: 'video' | 'image' | 'audio'
  duration?: number
  date?: string
  resolution?: string
}

export interface MediaCluster {
  id: string
  label: string
  description?: string
  cover_thumbnail?: string
  file_count: number
  total_duration?: number
  date_range?: string
  media_types?: string[]
  folder_path?: string
}

interface FolderEntry {
  path: string
  name: string
  type: 'folder' | 'video' | 'image' | 'audio' | 'other'
  children_count?: number
  media_count?: number
  thumbnail_url?: string
  size_bytes?: number
  depth?: number
}

// --- Utility ---

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  if (m < 60) return `${m}:${s.toString().padStart(2, '0')}`
  const h = Math.floor(m / 60)
  return `${h}:${(m % 60).toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const mediaIcon = (type: string, size = 12) => {
  switch (type) {
    case 'video': return <Film size={size} />
    case 'image': return <ImageIcon size={size} />
    case 'audio': return <Music size={size} />
    case 'folder': return <Folder size={size} />
    default: return <HardDrive size={size} />
  }
}

// --- MediaGrid ---

export function MediaGrid({
  items,
  selectionMode = 'multi',
  label,
  onSelect,
  onConfirmSelection,
}: {
  items: MediaItem[]
  selectionMode?: 'single' | 'multi' | 'none'
  label?: string
  onSelect?: (paths: string[]) => void
  onConfirmSelection?: (message: string) => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmed, setConfirmed] = useState(false)

  const toggle = useCallback((path: string) => {
    if (selectionMode === 'none' || confirmed) return
    setSelected(prev => {
      const next = new Set(prev)
      if (selectionMode === 'single') {
        if (next.has(path)) {
          next.clear()
        } else {
          next.clear()
          next.add(path)
        }
      } else {
        if (next.has(path)) next.delete(path)
        else next.add(path)
      }
      onSelect?.(Array.from(next))
      return next
    })
  }, [selectionMode, onSelect, confirmed])

  const handleConfirm = useCallback(() => {
    if (selected.size === 0 || !onConfirmSelection) return
    const selectedItems = items.filter(i => selected.has(i.path))
    const names = selectedItems.map(i => i.name).join(', ')
    onConfirmSelection(`I selected these files: ${names}`)
    setConfirmed(true)
  }, [selected, items, onConfirmSelection])

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
        {items.map((item) => (
          <button
            key={item.path}
            onClick={() => toggle(item.path)}
            className={cn(
              'group relative aspect-video rounded-md overflow-hidden bg-muted border transition-all',
              selected.has(item.path)
                ? 'border-blue-500 ring-1 ring-blue-500/50'
                : 'border-border hover:border-border/80',
              selectionMode === 'none' && 'cursor-default'
            )}
          >
            {item.thumbnail_url ? (
              <img
                src={item.thumbnail_url}
                alt={item.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                {mediaIcon(item.type, 24)}
              </div>
            )}
            {/* Selection indicator */}
            {selectionMode !== 'none' && selected.has(item.path) && (
              <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                <Check size={12} className="text-white" />
              </div>
            )}
            {/* Duration badge */}
            {item.duration != null && (
              <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded text-[10px] font-mono bg-black/70 text-white">
                {formatDuration(item.duration)}
              </div>
            )}
            {/* Type icon */}
            <div className="absolute bottom-1 left-1 text-white/60">
              {mediaIcon(item.type, 10)}
            </div>
            {/* Name tooltip on hover */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-[10px] text-white truncate">{item.name}</p>
            </div>
          </button>
        ))}
      </div>
      {selectionMode !== 'none' && selected.size > 0 && (
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground">{selected.size} selected</p>
          {onConfirmSelection && !confirmed && (
            <button
              onClick={handleConfirm}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              <Send size={10} />
              Confirm selection
            </button>
          )}
          {confirmed && (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <Check size={10} />
              Sent
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// --- ClusterCards ---

export function ClusterCards({
  clusters,
  layout = 'horizontal',
  label,
  onSelect,
  onConfirmSelection,
}: {
  clusters: MediaCluster[]
  layout?: 'horizontal' | 'vertical'
  label?: string
  onSelect?: (ids: string[]) => void
  onConfirmSelection?: (message: string) => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmed, setConfirmed] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const toggle = useCallback((id: string) => {
    if (confirmed) return
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      onSelect?.(Array.from(next))
      return next
    })
  }, [onSelect, confirmed])

  const handleConfirm = useCallback(() => {
    if (selected.size === 0 || !onConfirmSelection) return
    const selectedClusters = clusters.filter(c => selected.has(c.id))
    const desc = selectedClusters.map(c => {
      const parts = [c.label, `${c.file_count} files`]
      if (c.date_range) parts.push(c.date_range)
      return parts.join(' — ')
    }).join('; ')
    onConfirmSelection(`I selected these groups: ${desc}`)
    setConfirmed(true)
  }, [selected, clusters, onConfirmSelection])

  if (layout === 'vertical') {
    return (
      <div className="space-y-3">
        {label && (
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
        )}
        <div className="grid gap-3">
          {clusters.map((cluster) => (
            <button
              key={cluster.id}
              onClick={() => toggle(cluster.id)}
              className={cn(
                'group w-full rounded-xl overflow-hidden border transition-all text-left',
                'flex flex-row',
                selected.has(cluster.id)
                  ? 'border-blue-500 ring-1 ring-blue-500/30 bg-blue-500/5'
                  : 'border-surface-border hover:border-surface-border/80 bg-surface/50'
              )}
            >
              <div className="relative w-44 shrink-0 bg-surface"
                onClick={(e) => {
                  if (cluster.cover_thumbnail) {
                    e.stopPropagation()
                    setLightboxIndex(clusters.indexOf(cluster))
                  }
                }}
              >
                {cluster.cover_thumbnail ? (
                  <img
                    src={cluster.cover_thumbnail}
                    alt={cluster.label}
                    className="w-full h-full object-cover aspect-video"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center text-surface-border">
                    <Folder size={36} />
                  </div>
                )}
                {selected.has(cluster.id) && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                )}
                <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono bg-black/70 text-white">
                  {cluster.file_count} files
                </div>
              </div>
              <div className="flex-1 p-3 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{cluster.label}</p>
                {cluster.description && (
                  <p className="text-xs text-surface-muted mt-1 line-clamp-2">{cluster.description}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-[11px] text-surface-muted">
                  {cluster.date_range && (
                    <span className="flex items-center gap-1">
                      <Clock size={10} />
                      {cluster.date_range}
                    </span>
                  )}
                  {cluster.total_duration != null && (
                    <span>{formatDuration(cluster.total_duration)}</span>
                  )}
                  {cluster.media_types && cluster.media_types.length > 0 && (
                    <span className="flex gap-1">
                      {cluster.media_types.map(t => (
                        <span key={t}>{mediaIcon(t, 10)}</span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <p className="text-xs text-surface-muted">{selected.size} cluster{selected.size !== 1 ? 's' : ''} selected</p>
            {onConfirmSelection && !confirmed && (
              <button
                onClick={handleConfirm}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              >
                <Send size={10} />
                Confirm selection
              </button>
            )}
            {confirmed && (
              <span className="flex items-center gap-1 text-xs text-green-500">
                <Check size={10} />
                Sent
              </span>
            )}
          </div>
        )}
        {lightboxIndex !== null && (
          <ClusterLightbox
            clusters={clusters}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
      )}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {clusters.map((cluster) => (
          <button
            key={cluster.id}
            onClick={() => toggle(cluster.id)}
            className={cn(
              'flex-shrink-0 w-48 rounded-lg overflow-hidden border transition-all snap-start',
              selected.has(cluster.id)
                ? 'border-blue-500 ring-1 ring-blue-500/50'
                : 'border-border hover:border-border/80'
            )}
          >
            <div className="aspect-video bg-muted relative">
              {cluster.cover_thumbnail ? (
                <img
                  src={cluster.cover_thumbnail}
                  alt={cluster.label}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground/50">
                  <Folder size={32} />
                </div>
              )}
              {selected.has(cluster.id) && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
              <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono bg-black/70 text-white">
                {cluster.file_count} files
              </div>
            </div>
            <div className="p-2 bg-card text-left">
              <p className="text-sm font-medium text-foreground truncate">{cluster.label}</p>
              {cluster.description && (
                <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{cluster.description}</p>
              )}
              <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                {cluster.date_range && (
                  <span className="flex items-center gap-0.5">
                    <Clock size={9} />
                    {cluster.date_range}
                  </span>
                )}
                {cluster.total_duration != null && (
                  <span>{formatDuration(cluster.total_duration)}</span>
                )}
              </div>
              {cluster.media_types && cluster.media_types.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {cluster.media_types.map(t => (
                    <span key={t} className="text-muted-foreground">{mediaIcon(t, 10)}</span>
                  ))}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      {selected.size > 0 && (
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground">{selected.size} cluster{selected.size !== 1 ? 's' : ''} selected</p>
          {onConfirmSelection && !confirmed && (
            <button
              onClick={handleConfirm}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              <Send size={10} />
              Confirm selection
            </button>
          )}
          {confirmed && (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <Check size={10} />
              Sent
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// --- ClusterLightbox ---

function ClusterLightbox({
  clusters,
  index,
  onClose,
  onNavigate,
}: {
  clusters: MediaCluster[]
  index: number
  onClose: () => void
  onNavigate: (index: number) => void
}) {
  const cluster = clusters[index]
  if (!cluster) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
      >
        <X size={20} />
      </button>

      {clusters.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate((index - 1 + clusters.length) % clusters.length) }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate((index + 1) % clusters.length) }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
          >
            <ChevronRightIcon size={20} />
          </button>
        </>
      )}

      <div className="max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-xl overflow-hidden bg-surface border border-surface-border">
          {cluster.cover_thumbnail ? (
            <img
              src={cluster.cover_thumbnail}
              alt={cluster.label}
              className="w-full aspect-video object-cover"
            />
          ) : (
            <div className="w-full aspect-video flex items-center justify-center bg-surface text-surface-border">
              <Folder size={64} />
            </div>
          )}
          <div className="p-4">
            <h3 className="text-lg font-semibold text-white">{cluster.label}</h3>
            {cluster.description && (
              <p className="text-sm text-surface-muted mt-1">{cluster.description}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-surface-muted">
              <span>{cluster.file_count} files</span>
              {cluster.date_range && (
                <span className="flex items-center gap-1">
                  <Clock size={11} />
                  {cluster.date_range}
                </span>
              )}
              {cluster.total_duration != null && (
                <span>{formatDuration(cluster.total_duration)}</span>
              )}
              {cluster.folder_path && (
                <span className="font-mono text-[10px] text-surface-muted truncate">{cluster.folder_path}</span>
              )}
            </div>
          </div>
        </div>

        {clusters.length > 1 && (
          <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1 justify-center">
            {clusters.map((c, i) => (
              <button
                key={c.id}
                onClick={() => onNavigate(i)}
                className={cn(
                  'w-16 h-10 rounded-md overflow-hidden border-2 shrink-0 transition-all',
                  i === index ? 'border-blue-500 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                )}
              >
                {c.cover_thumbnail ? (
                  <img src={c.cover_thumbnail} alt={c.label} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-surface-elevated flex items-center justify-center text-surface-muted">
                    <Folder size={12} />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// --- FolderPreview ---

export function FolderPreview({
  rootPath,
  tree,
  label,
}: {
  rootPath: string
  tree: FolderEntry[]
  label?: string
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggleExpand = (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const rootName = rootPath.split('/').filter(Boolean).pop() || rootPath

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
      )}
      <div className="rounded-lg border border-border bg-card overflow-hidden text-sm">
        {/* Root header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/50">
          <Folder size={14} className="text-muted-foreground" />
          <span className="text-foreground font-medium truncate">{rootName}</span>
          <span className="text-[10px] text-muted-foreground ml-auto">{rootPath}</span>
        </div>
        {/* Tree entries */}
        <div className="max-h-64 overflow-y-auto">
          {tree.map((entry) => {
            const isFolder = entry.type === 'folder'
            const isExpanded = expanded.has(entry.path)
            const depth = entry.depth ?? 0

            return (
              <div
                key={entry.path}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 transition-colors',
                  isFolder && 'cursor-pointer'
                )}
                style={{ paddingLeft: `${12 + depth * 16}px` }}
                onClick={() => isFolder && toggleExpand(entry.path)}
              >
                {isFolder && (
                  <ChevronRight
                    size={12}
                    className={cn('text-muted-foreground transition-transform', isExpanded && 'rotate-90')}
                  />
                )}
                <span className={cn('text-muted-foreground', !isFolder && 'ml-4')}>
                  {mediaIcon(entry.type, 12)}
                </span>
                <span className="text-foreground truncate flex-1">{entry.name}</span>
                {isFolder && entry.media_count != null && (
                  <span className="text-[10px] text-muted-foreground">{entry.media_count} media</span>
                )}
                {!isFolder && entry.size_bytes != null && (
                  <span className="text-[10px] text-muted-foreground">{formatBytes(entry.size_bytes)}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// --- QuestionOptions ---

interface QuestionOption {
  id: string
  label: string
  description?: string
  image_url?: string
  preview_url?: string
  recommended?: boolean
  swatches?: string[]
  aspect?: string
  caption_preset?: string
}

// Wire constants shared with the agent prompt: an answer of DECIDE_FOR_ME means
// "use your recommended option"; a STOP_ASKING line flips the project to
// surprise_me autonomy. Keep in sync with the PRESENTING CHOICES prompt section.
export const DECIDE_FOR_ME = 'Decide for me'
export const STOP_ASKING = 'Stop asking — make the remaining decisions yourself and proceed.'

const CATEGORY_LABELS: Record<string, string> = {
  format: 'Format',
  story: 'Story',
  cast: 'Casting',
  world: 'Setting',
  product: 'Product',
  look: 'The Look',
  sound: 'Sound',
  type: 'Type',
  process: 'How we work',
}

function CategoryTag({ category, prefix }: { category?: string; prefix?: string }) {
  if (!category && !prefix) return null
  const label = category ? (CATEGORY_LABELS[category] ?? category) : null
  return (
    <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground/80">
      {[prefix, label].filter(Boolean).join(' · ')}
    </span>
  )
}

const optionHasVisual = (o: QuestionOption) =>
  !!(o.image_url || o.swatches?.length || o.aspect || o.caption_preset)

const asArray = (v: unknown): unknown[] => {
  if (Array.isArray(v)) return v
  if (typeof v === 'string') try { return JSON.parse(v) } catch { return [] }
  return []
}

/**
 * Whether a tool widget benefits from breaking out of the readable text column
 * into a wider lane: rich visual content (treatment board, media grids) or
 * option sets that carry image/swatch/aspect/caption cards. Text-only questions
 * stay narrow — they read better in a tight column.
 */
export function widgetWantsWideLane(data: Record<string, unknown> | null | undefined): boolean {
  const type = (data as { type?: string } | null | undefined)?.type
  if (!type) return false
  if (['treatment_board', 'media_grid', 'media_clusters', 'folder_preview'].includes(type)) return true
  if (type === 'question_options') {
    return asArray((data as { options?: unknown }).options).some(o => optionHasVisual(o as QuestionOption))
  }
  if (type === 'question_sequence') {
    return asArray((data as { questions?: unknown }).questions).some(q =>
      asArray((q as { options?: unknown }).options).some(o => optionHasVisual(o as QuestionOption)),
    )
  }
  return false
}

const pickRecommended = (options: QuestionOption[]) =>
  options.find(o => o.recommended) ?? options[0]

function AspectFrame({ aspect }: { aspect: string }) {
  const [w, h] = aspect.split(':').map(Number)
  const ratio = w > 0 && h > 0 ? w / h : 1
  let fw = 72
  let fh = fw / ratio
  if (fh > 72) { fh = 72; fw = fh * ratio }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
      <div className="border-2 border-foreground/50 rounded-[3px] bg-foreground/5" style={{ width: fw, height: fh }} />
      <span className="text-[10px] font-medium text-muted-foreground">{aspect}</span>
    </div>
  )
}

/**
 * One option card — shared by QuestionOptions and QuestionSequence. Renders
 * whichever visual the option carries: image, palette swatches, aspect frame
 * diagram, or a live caption sample.
 */
function OptionCard({
  opt,
  isSelected,
  dimmed,
  inGrid,
  tallVisual,
  showRecommended,
  onSelect,
  onZoom,
  playingId,
  onTogglePreview,
}: {
  opt: QuestionOption
  isSelected: boolean
  dimmed: boolean
  inGrid: boolean
  tallVisual: boolean
  showRecommended: boolean
  onSelect: (id: string) => void
  onZoom?: (src: string, alt: string) => void
  playingId: string | null
  onTogglePreview: (id: string, url: string, e: React.MouseEvent) => void
}) {
  return (
    <div
      className={cn(
        inGrid ? 'w-full' : 'flex-shrink-0 w-44 snap-start',
        'rounded-lg overflow-hidden border transition-all text-left',
        isSelected
          ? 'border-blue-500 ring-1 ring-blue-500/50'
          : 'border-border hover:border-foreground/20',
        dimmed && 'opacity-40 pointer-events-none',
      )}
    >
      <div
        className={cn(tallVisual ? 'aspect-[3/4]' : 'aspect-[4/3]', 'bg-muted relative', opt.image_url && !dimmed && 'cursor-zoom-in group/img')}
        onClick={() => opt.image_url && !dimmed && onZoom?.(opt.image_url, opt.label)}
      >
        {opt.image_url ? (
          <img src={opt.image_url} alt={opt.label} className="w-full h-full object-cover" loading="lazy" />
        ) : opt.swatches?.length ? (
          <div className="absolute inset-0 flex flex-col">
            {opt.swatches.map((c, i) => (
              <div key={i} className="flex-1" style={{ backgroundColor: c }} />
            ))}
          </div>
        ) : opt.caption_preset ? (
          <div className="absolute inset-0 bg-[#15171c] flex items-center justify-center p-2 overflow-hidden">
            <CaptionSampleBox presetName={opt.caption_preset} />
          </div>
        ) : opt.aspect ? (
          <AspectFrame aspect={opt.aspect} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
            <ImageIcon size={24} />
          </div>
        )}
        {showRecommended && opt.recommended && (
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[9px] font-semibold tracking-wide shadow-sm">
            Our pick
          </div>
        )}
        {isSelected && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
        )}
        {opt.image_url && !dimmed && (
          <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover/img:opacity-100">
            <Maximize2 size={20} className="text-white drop-shadow-lg" />
          </div>
        )}
        {opt.preview_url && (
          <button
            onClick={(e) => onTogglePreview(opt.id, opt.preview_url!, e)}
            className="absolute bottom-1.5 left-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          >
            {playingId === opt.id ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
          </button>
        )}
      </div>
      <button
        onClick={() => onSelect(opt.id)}
        disabled={dimmed}
        className="w-full p-2 bg-card text-left hover:bg-muted/50 transition-colors"
      >
        <p className="text-sm font-medium text-foreground truncate">{opt.label}</p>
        {opt.description && (
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{opt.description}</p>
        )}
      </button>
    </div>
  )
}

/** Grid for few options, snap-scroll strip for many. Literal classes only — Tailwind can't see computed names. */
function optionLayoutClass(count: number): { className: string; inGrid: boolean } {
  if (count <= 2) return { className: 'grid grid-cols-2 gap-2', inGrid: true }
  if (count === 3) return { className: 'grid grid-cols-3 gap-2', inGrid: true }
  if (count === 4) return { className: 'grid grid-cols-2 sm:grid-cols-4 gap-2', inGrid: true }
  return { className: 'flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x', inGrid: false }
}

function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
        <X size={18} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      />
    </div>
  )
}

export function QuestionOptions({
  question,
  category,
  options,
  selectionMode = 'single',
  onConfirmSelection,
  existingAnswer,
}: {
  question: string
  category?: string
  options: QuestionOption[]
  selectionMode?: 'single' | 'multi'
  onConfirmSelection?: (message: string) => void
  existingAnswer?: string
}) {
  useCaptionPreviewFonts()

  // Parse existing answer for reload persistence. An answer of DECIDE_FOR_ME
  // resolves to the recommended option (delegated to the agent).
  const parsedExisting = useMemo(() => {
    if (!existingAnswer) return null
    const text = existingAnswer.replace(/^\u200B/, '')
    if (text === DECIDE_FOR_ME) {
      const rec = pickRecommended(options)
      return rec ? { ids: new Set([rec.id]), delegated: true } : null
    }
    const labels = text.split(', ').map(l => l.trim())
    const ids = new Set(options.filter(o => labels.includes(o.label)).map(o => o.id))
    return ids.size > 0 ? { ids, delegated: false } : null
  }, [existingAnswer, options])

  const [selected, setSelected] = useState<Set<string>>(parsedExisting?.ids || new Set())
  const [confirmed, setConfirmed] = useState(!!parsedExisting)
  const [delegated, setDelegated] = useState(parsedExisting?.delegated ?? false)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<{ src: string; alt: string } | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Cleanup audio on unmount
  useEffect(() => {
    return () => { audioRef.current?.pause() }
  }, [])

  const hasVisual = options.some(optionHasVisual)
  const tallVisual = options.some(o => o.image_url)

  const handleSelect = useCallback((id: string) => {
    if (confirmed) return
    if (selectionMode === 'single') {
      const opt = options.find(o => o.id === id)
      if (!opt || !onConfirmSelection) return
      // Stop any playing audio
      audioRef.current?.pause()
      setPlayingId(null)
      setSelected(new Set([id]))
      setConfirmed(true)
      onConfirmSelection(`\u200B${opt.label}`)
    } else {
      setSelected(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    }
  }, [selectionMode, confirmed, options, onConfirmSelection])

  const handleConfirmMulti = useCallback(() => {
    if (selected.size === 0 || !onConfirmSelection) return
    const chosen = options.filter(o => selected.has(o.id))
    onConfirmSelection(`\u200B${chosen.map(o => o.label).join(', ')}`)
    setConfirmed(true)
  }, [selected, options, onConfirmSelection])

  const handleDecideForMe = useCallback(() => {
    if (confirmed || !onConfirmSelection) return
    const rec = pickRecommended(options)
    if (!rec) return
    audioRef.current?.pause()
    setPlayingId(null)
    setSelected(new Set([rec.id]))
    setDelegated(true)
    setConfirmed(true)
    onConfirmSelection(`\u200B${DECIDE_FOR_ME}`)
  }, [confirmed, options, onConfirmSelection])

  const togglePreview = useCallback((id: string, url: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (playingId === id) {
      audioRef.current?.pause()
      setPlayingId(null)
    } else {
      audioRef.current?.pause()
      const audio = new Audio(url)
      audioRef.current = audio
      audio.onended = () => setPlayingId(null)
      audio.play()
      setPlayingId(id)
    }
  }, [playingId])

  // Card layout for options with any visual (image, swatches, aspect, caption)
  if (hasVisual) {
    const { className: layoutCls, inGrid } = optionLayoutClass(options.length)
    return (
      <div className="space-y-2.5">
        <CategoryTag category={category} />
        <p className="text-sm font-medium text-foreground">{question}</p>
        <div className={layoutCls}>
          {options.map(opt => (
            <OptionCard
              key={opt.id}
              opt={opt}
              isSelected={selected.has(opt.id)}
              dimmed={confirmed && !selected.has(opt.id)}
              inGrid={inGrid}
              tallVisual={tallVisual}
              showRecommended={!confirmed}
              onSelect={handleSelect}
              onZoom={(src, alt) => !confirmed && setLightboxUrl({ src, alt })}
              playingId={playingId}
              onTogglePreview={togglePreview}
            />
          ))}
        </div>
        {selectionMode === 'multi' && selected.size > 0 && !confirmed && (
          <button
            onClick={handleConfirmMulti}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
          >
            <Send size={10} />
            Confirm ({selected.size})
          </button>
        )}
        {!confirmed && selectionMode === 'single' && (
          <div className="flex justify-end">
            <button
              onClick={handleDecideForMe}
              className="flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-600 transition-colors"
            >
              {DECIDE_FOR_ME} <ArrowRight size={11} />
            </button>
          </div>
        )}
        {confirmed && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check size={10} className="text-green-500" />
            {options.filter(o => selected.has(o.id)).map(o => o.label).join(', ')}
            {delegated && ' — agent’s pick'}
          </span>
        )}
        {lightboxUrl && <ImageLightbox src={lightboxUrl.src} alt={lightboxUrl.alt} onClose={() => setLightboxUrl(null)} />}
      </div>
    )
  }

  // List layout for text-only options
  return (
    <div className="space-y-2">
      <CategoryTag category={category} />
      <p className="text-sm font-medium text-foreground">{question}</p>
      <div className="flex flex-col gap-1">
        {options.map(opt => (
          <div key={opt.id} className="flex items-start gap-2.5">
            {opt.preview_url && (
              <button
                onClick={(e) => togglePreview(opt.id, opt.preview_url!, e)}
                className={cn(
                  'mt-1 w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all border',
                  playingId === opt.id
                    ? 'bg-blue-500 border-blue-500 text-white scale-110'
                    : 'bg-muted/80 border-border hover:bg-blue-500/10 hover:border-blue-500/40 text-muted-foreground hover:text-blue-500',
                )}
              >
                {playingId === opt.id ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
              </button>
            )}
            <button
              onClick={() => handleSelect(opt.id)}
              disabled={confirmed && !selected.has(opt.id)}
              className={cn(
                'group flex items-start gap-2.5 flex-1 min-w-0 rounded-lg px-3 py-2 text-left transition-all border',
                selected.has(opt.id)
                  ? 'border-blue-500/60 bg-blue-500/10'
                  : 'border-transparent hover:bg-muted/50',
                confirmed && !selected.has(opt.id) && 'opacity-40 pointer-events-none',
              )}
            >
              <div className={cn(
                'mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                selected.has(opt.id)
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-muted-foreground/30 group-hover:border-muted-foreground/50',
              )}>
                {selected.has(opt.id) && <Check size={10} className="text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {opt.label}
                  {opt.recommended && !confirmed && (
                    <span className="ml-1.5 px-1.5 py-px rounded-full bg-blue-500/10 text-blue-500 text-[9px] font-semibold tracking-wide align-middle">
                      Our pick
                    </span>
                  )}
                </p>
                {opt.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                )}
              </div>
            </button>
          </div>
        ))}
      </div>
      {selectionMode === 'multi' && selected.size > 0 && !confirmed && (
        <button
          onClick={handleConfirmMulti}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          <Send size={10} />
          Confirm ({selected.size})
        </button>
      )}
      {!confirmed && selectionMode === 'single' && (
        <div className="flex justify-end">
          <button
            onClick={handleDecideForMe}
            className="flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-600 transition-colors"
          >
            {DECIDE_FOR_ME} <ArrowRight size={11} />
          </button>
        </div>
      )}
      {confirmed && (selectionMode === 'multi' || delegated) && (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Check size={10} className="text-green-500" />
          {options.filter(o => selected.has(o.id)).map(o => o.label).join(', ')}
          {delegated && ' — agent’s pick'}
        </span>
      )}
    </div>
  )
}

// --- QuestionSequence ---

interface SequenceQuestion {
  id: string
  question: string
  category?: string
  options: QuestionOption[]
  selection_mode?: 'single' | 'multi'
  allow_text?: boolean
}

// Mid-sequence progress survives widget remounts (the assistant message swaps
// from its streaming to its persisted form when the stream completes, which
// remounts the whole bubble). Keyed by tool-call id; memory-only — after the
// batch submits, reload-persistence comes from existingAnswer instead.
const sequenceProgress = new Map<string, {
  answers: Array<[string, string]>
  index: number
  done: boolean
  stopped: boolean
}>()

export function QuestionSequence({
  questions,
  onConfirmSelection,
  existingAnswer,
  cacheKey,
}: {
  questions: SequenceQuestion[]
  onConfirmSelection?: (message: string) => void
  existingAnswer?: string
  cacheKey?: string
}) {
  useCaptionPreviewFonts()

  // Parse existing answer for reload persistence. A STOP_ASKING line means the
  // user delegated everything from that point on.
  const parsedExisting = useMemo(() => {
    if (!existingAnswer) return null
    const text = existingAnswer.replace(/^\u200B/, '')
    const lines = text.split('\n')
    const stopped = lines.some(l => l.trim() === STOP_ASKING)
    const map = new Map<string, string>()
    questions.forEach((q, i) => {
      const line = lines[i]
      if (line) {
        const prefix = `${q.question}: `
        if (line.startsWith(prefix)) {
          map.set(q.id, line.slice(prefix.length))
        }
      }
    })
    return map.size > 0 || stopped ? { map, stopped } : null
  }, [existingAnswer, questions])

  const cached = cacheKey ? sequenceProgress.get(cacheKey) : undefined
  const [currentIndex, setCurrentIndex] = useState(
    parsedExisting ? questions.length : cached?.index ?? 0,
  )
  const [answers, setAnswers] = useState<Map<string, string>>(
    parsedExisting?.map || (cached ? new Map(cached.answers) : new Map()),
  )
  const [done, setDone] = useState(!!parsedExisting || (cached?.done ?? false))
  const [stopped, setStopped] = useState(parsedExisting?.stopped ?? cached?.stopped ?? false)
  const [textValue, setTextValue] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<{ src: string; alt: string } | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    return () => { audioRef.current?.pause() }
  }, [])

  const saveProgress = useCallback((next: Map<string, string>, index: number, isDone: boolean, isStopped: boolean) => {
    if (!cacheKey) return
    sequenceProgress.set(cacheKey, { answers: [...next.entries()], index, done: isDone, stopped: isStopped })
  }, [cacheKey])

  const current = questions[currentIndex]
  const total = questions.length
  const isMulti = current?.selection_mode === 'multi'

  const submitAll = useCallback((allAnswers: Map<string, string>) => {
    if (!onConfirmSelection) return
    const lines = questions.map(q => {
      const ans = allAnswers.get(q.id) || ''
      return `${q.question}: ${ans}`
    })
    onConfirmSelection(`\u200B${lines.join('\n')}`)
    setDone(true)
    saveProgress(allAnswers, questions.length, true, false)
  }, [questions, onConfirmSelection, saveProgress])

  const advance = useCallback((questionId: string, answer: string) => {
    audioRef.current?.pause()
    setPlayingId(null)
    setTextValue('')
    setSelected(new Set())
    const next = new Map(answers)
    next.set(questionId, answer)
    setAnswers(next)
    if (currentIndex + 1 < total) {
      setCurrentIndex(currentIndex + 1)
      saveProgress(next, currentIndex + 1, false, false)
    } else {
      submitAll(next)
    }
  }, [answers, currentIndex, total, submitAll, saveProgress])

  const handleDecideForMe = useCallback(() => {
    if (done || !current) return
    advance(current.id, DECIDE_FOR_ME)
  }, [done, current, advance])

  // Hand everything from here on to the agent: submit the answers collected so
  // far plus the stop directive (the agent flips autonomy to surprise_me).
  const handleStopAsking = useCallback(() => {
    if (done || !onConfirmSelection) return
    audioRef.current?.pause()
    setPlayingId(null)
    const lines = questions
      .filter(q => answers.has(q.id))
      .map(q => `${q.question}: ${answers.get(q.id)}`)
    lines.push(STOP_ASKING)
    onConfirmSelection(`\u200B${lines.join('\n')}`)
    setStopped(true)
    setDone(true)
    saveProgress(answers, currentIndex, true, true)
  }, [done, onConfirmSelection, questions, answers, saveProgress, currentIndex])

  const handleOptionSelect = useCallback((id: string) => {
    if (done || !current) return
    if (isMulti) {
      setSelected(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    } else {
      const opt = current.options.find(o => o.id === id)
      if (!opt) return
      advance(current.id, opt.label)
    }
  }, [done, current, isMulti, advance])

  const handleConfirmMulti = useCallback(() => {
    if (!current || selected.size === 0) return
    const chosen = current.options.filter(o => selected.has(o.id))
    advance(current.id, chosen.map(o => o.label).join(', '))
  }, [current, selected, advance])

  const handleTextSubmit = useCallback(() => {
    if (done || !current || !textValue.trim()) return
    advance(current.id, textValue.trim())
  }, [done, current, textValue, advance])

  const togglePreview = useCallback((id: string, url: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (playingId === id) {
      audioRef.current?.pause()
      setPlayingId(null)
    } else {
      audioRef.current?.pause()
      const audio = new Audio(url)
      audioRef.current = audio
      audio.onended = () => setPlayingId(null)
      audio.play()
      setPlayingId(id)
    }
  }, [playingId])

  // Done state — show summary
  if (done) {
    return (
      <div className="space-y-1.5">
        {questions.map(q => {
          const ans = answers.get(q.id)
          if (!ans && stopped) return null
          const isDelegated = ans === DECIDE_FOR_ME
          const recLabel = isDelegated ? pickRecommended(q.options)?.label : undefined
          return (
            <div key={q.id} className="flex items-baseline gap-2 text-xs">
              <span className="text-muted-foreground shrink-0">{q.question}:</span>
              <span className="text-foreground font-medium">
                {isDelegated ? (recLabel ? `${recLabel} — agent’s pick` : 'Agent’s pick') : ans}
              </span>
            </div>
          )
        })}
        {stopped && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <FastForward size={11} className="text-blue-500" />
            Remaining decisions handed to the agent
          </div>
        )}
      </div>
    )
  }

  if (!current) return null

  const hasVisual = current.options.some(optionHasVisual)
  const tallVisual = current.options.some(o => o.image_url)
  const { className: layoutCls, inGrid } = optionLayoutClass(current.options.length)

  return (
    <div className="space-y-3">
      {/* Step indicator */}
      <div className="flex items-center gap-1.5">
        {questions.map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-1 rounded-full transition-all duration-300',
              i < currentIndex ? 'w-4 bg-blue-500' :
              i === currentIndex ? 'w-6 bg-blue-500' :
              'w-4 bg-muted-foreground/20',
            )}
          />
        ))}
        <span className="ml-1.5">
          <CategoryTag category={current.category} prefix={`${currentIndex + 1}/${total}`} />
        </span>
      </div>

      {/* Previous answers (compact) */}
      {currentIndex > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {questions.slice(0, currentIndex).map(q => (
            <span key={q.id} className="text-[11px] text-muted-foreground">
              <Check size={9} className="inline text-green-500 mr-0.5" />
              {answers.get(q.id)}
            </span>
          ))}
        </div>
      )}

      {/* Current question */}
      <p className="text-sm font-medium text-foreground">{current.question}</p>

      {/* Options */}
      {hasVisual ? (
        <>
        <div className={layoutCls}>
          {current.options.map(opt => (
            <OptionCard
              key={opt.id}
              opt={opt}
              isSelected={selected.has(opt.id)}
              dimmed={false}
              inGrid={inGrid}
              tallVisual={tallVisual}
              showRecommended
              onSelect={handleOptionSelect}
              onZoom={(src, alt) => setLightboxUrl({ src, alt })}
              playingId={playingId}
              onTogglePreview={togglePreview}
            />
          ))}
        </div>
        {lightboxUrl && <ImageLightbox src={lightboxUrl.src} alt={lightboxUrl.alt} onClose={() => setLightboxUrl(null)} />}
        </>
      ) : (
        <div className="flex flex-col gap-1">
          {current.options.map(opt => (
            <div key={opt.id} className="flex items-start gap-2.5">
              {opt.preview_url && (
                <button
                  onClick={(e) => togglePreview(opt.id, opt.preview_url!, e)}
                  className={cn(
                    'mt-1 w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all border',
                    playingId === opt.id
                      ? 'bg-blue-500 border-blue-500 text-white scale-110'
                      : 'bg-muted/80 border-border hover:bg-blue-500/10 hover:border-blue-500/40 text-muted-foreground hover:text-blue-500',
                  )}
                >
                  {playingId === opt.id ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
                </button>
              )}
              <button
                onClick={() => handleOptionSelect(opt.id)}
                className={cn(
                  'group flex items-start gap-2.5 flex-1 min-w-0 rounded-lg px-3 py-2 text-left transition-all border',
                  selected.has(opt.id)
                    ? 'border-blue-500/60 bg-blue-500/10'
                    : 'border-transparent hover:bg-muted/50',
                )}
              >
                <div className={cn(
                  'mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                  selected.has(opt.id)
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-muted-foreground/30 group-hover:border-muted-foreground/50',
                )}>
                  {selected.has(opt.id) && <Check size={10} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {opt.label}
                    {opt.recommended && (
                      <span className="ml-1.5 px-1.5 py-px rounded-full bg-blue-500/10 text-blue-500 text-[9px] font-semibold tracking-wide align-middle">
                        Our pick
                      </span>
                    )}
                  </p>
                  {opt.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Multi-select confirm */}
      {isMulti && selected.size > 0 && (
        <button
          onClick={handleConfirmMulti}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          <Send size={10} />
          Confirm ({selected.size})
        </button>
      )}

      {/* Text input — shown by default unless explicitly disabled */}
      {current.allow_text !== false && (
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleTextSubmit() }}
            placeholder="Or type your own answer..."
            className="flex-1 text-sm bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-blue-500/50"
          />
          {textValue.trim() && (
            <button
              onClick={handleTextSubmit}
              className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors"
            >
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}

      {/* Delegation row: skip one question, or hand the rest to the agent */}
      <div className="flex items-center justify-between pt-0.5">
        <button
          onClick={handleStopAsking}
          className="flex items-center gap-1 text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
        >
          <FastForward size={10} />
          Stop asking — just make it
        </button>
        {!isMulti && (
          <button
            onClick={handleDecideForMe}
            className="flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-600 transition-colors"
          >
            {DECIDE_FOR_ME} <ArrowRight size={11} />
          </button>
        )}
      </div>
    </div>
  )
}

// --- AssetRequestCard ---

interface AssetRequestItem {
  id: string
  kind?: 'image' | 'video' | 'audio' | 'document' | 'any'
  label: string
  description?: string
  generatable?: boolean
}

const ASSET_KIND_ACCEPT: Record<string, string> = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
  document: '.pdf,.txt,.md,.doc,.docx,.csv',
  any: '*',
}

function assetKindIcon(kind?: string) {
  switch (kind) {
    case 'image': return <ImageIcon size={14} />
    case 'video': return <Film size={14} />
    case 'audio': return <Music size={14} />
    case 'document': return <FileText size={14} />
    default: return <Folder size={14} />
  }
}

// Per-item resolution survives remounts (assistant message swap on stream
// completion), same pattern as sequenceProgress. Memory-only.
const assetRequestState = new Map<string, { handled: Array<[string, 'uploaded' | 'generate']>; skipped: boolean }>()

/**
 * The agent's ask for real files. Each request row uploads directly into the
 * conversation (real assets beat generated ones); "Generate instead" and
 * "Skip" hand the decision back to the agent as plain messages.
 */
export function AssetRequestCard({
  requests,
  note,
  onSendSelection,
  onSendFiles,
  cacheKey,
}: {
  requests: AssetRequestItem[]
  note?: string
  onSendSelection?: (message: string) => void
  onSendFiles?: (message: string, files: File[]) => void
  cacheKey?: string
}) {
  const cached = cacheKey ? assetRequestState.get(cacheKey) : undefined
  const [handled, setHandled] = useState<Map<string, 'uploaded' | 'generate'>>(
    () => new Map(cached?.handled ?? []),
  )
  const [skipped, setSkipped] = useState(cached?.skipped ?? false)
  const [accept, setAccept] = useState('*')
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingItemRef = useRef<AssetRequestItem | null>(null)

  const persist = useCallback((nextHandled: Map<string, 'uploaded' | 'generate'>, nextSkipped: boolean) => {
    if (!cacheKey) return
    assetRequestState.set(cacheKey, { handled: [...nextHandled.entries()], skipped: nextSkipped })
  }, [cacheKey])

  const markHandled = useCallback((id: string, how: 'uploaded' | 'generate') => {
    setHandled(prev => {
      const next = new Map(prev)
      next.set(id, how)
      persist(next, false)
      return next
    })
  }, [persist])

  const openPicker = useCallback((item: AssetRequestItem) => {
    pendingItemRef.current = item
    setAccept(ASSET_KIND_ACCEPT[item.kind ?? 'any'] ?? '*')
    // Let the accept attribute land before opening the picker
    setTimeout(() => fileInputRef.current?.click(), 0)
  }, [])

  const handleFilesChosen = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const item = pendingItemRef.current
    const files = e.target.files ? Array.from(e.target.files) : []
    e.target.value = ''
    if (!item || files.length === 0 || !onSendFiles) return
    onSendFiles(`Here ${files.length > 1 ? 'are' : 'is'} my ${item.label.toLowerCase()}.`, files)
    markHandled(item.id, 'uploaded')
  }, [onSendFiles, markHandled])

  const handleGenerateInstead = useCallback((item: AssetRequestItem) => {
    onSendSelection?.(`I don't have ${item.label.toLowerCase()} — generate it instead.`)
    markHandled(item.id, 'generate')
  }, [onSendSelection, markHandled])

  const handleSkip = useCallback(() => {
    onSendSelection?.('Skip the uploads — proceed without them.')
    setSkipped(true)
    persist(handled, true)
  }, [onSendSelection, handled, persist])

  const allResolved = skipped || requests.every(r => handled.has(r.id))

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-3.5 py-2.5 border-b border-border flex items-center gap-2">
        <Upload size={13} className="text-blue-500 shrink-0" />
        <span className="text-[13px] font-semibold text-foreground">Your files make this better</span>
      </div>
      {note && <p className="px-3.5 pt-2.5 text-[12.5px] text-muted-foreground leading-snug">{note}</p>}
      <div className="p-2.5 space-y-1.5">
        {requests.map(item => {
          const state = handled.get(item.id)
          return (
            <div
              key={item.id}
              className={cn(
                'flex items-center gap-2.5 rounded-lg border border-border/60 px-2.5 py-2',
                (state || skipped) && 'opacity-60',
              )}
            >
              <span className="w-7 h-7 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                {assetKindIcon(item.kind)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-foreground truncate">{item.label}</div>
                {item.description && (
                  <div className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{item.description}</div>
                )}
              </div>
              {state === 'uploaded' ? (
                <span className="flex items-center gap-1 text-[11px] font-medium text-green-600 shrink-0"><Check size={11} /> Sent</span>
              ) : state === 'generate' ? (
                <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground shrink-0"><Sparkles size={11} /> Generating</span>
              ) : !skipped && (
                <div className="flex items-center gap-2 shrink-0">
                  {item.generatable !== false && (
                    <button
                      onClick={() => handleGenerateInstead(item)}
                      className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Generate instead
                    </button>
                  )}
                  <button
                    onClick={() => openPicker(item)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-[11.5px] font-medium transition-colors"
                  >
                    <Upload size={11} />
                    Upload
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {!allResolved && (
        <div className="px-3.5 pb-2.5 flex justify-end">
          <button
            onClick={handleSkip}
            className="flex items-center gap-1 text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          >
            <FastForward size={10} />
            Skip — proceed without
          </button>
        </div>
      )}
      <input ref={fileInputRef} type="file" accept={accept} multiple className="hidden" onChange={handleFilesChosen} />
    </div>
  )
}

// --- TreatmentBoard ---

const GREENLIGHT_MESSAGE = 'Greenlight — generate the video.'

function BoardTile({
  label,
  title,
  onOpen,
  children,
}: {
  label: string
  title: string
  onOpen?: () => void
  children: React.ReactNode
}) {
  return (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(e) => { if (onOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen() } }}
      className={cn(
        'rounded-lg border border-border overflow-hidden bg-card min-w-0',
        onOpen && 'cursor-pointer transition-all hover:border-blue-500/50 hover:shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
      )}
    >
      <div className="aspect-square bg-muted relative overflow-hidden">
        {children}
      </div>
      <div className="px-1.5 py-1">
        <div className="text-[8px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">{label}</div>
        <div className="text-[11px] font-medium text-foreground truncate leading-tight">{title}</div>
      </div>
    </div>
  )
}

/**
 * Detail overlay for a board tile or beat: full underlying data plus an
 * adjustment box that submits a targeted change request to the agent as a
 * normal chat message ("Change the setting: warmer evening light").
 */
function BoardDetailSheet({
  title,
  subject,
  onClose,
  onSendAdjustment,
  children,
}: {
  title: string
  subject: string
  onClose: () => void
  onSendAdjustment?: (message: string) => void
  children: React.ReactNode
}) {
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const submit = useCallback(() => {
    const t = text.trim()
    if (!t || !onSendAdjustment) return
    onSendAdjustment(`Change the ${subject}: ${t}`)
    setSent(true)
    setText('')
    setTimeout(onClose, 1100)
  }, [text, subject, onSendAdjustment, onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl w-full max-w-md max-h-[82vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X size={15} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">{children}</div>
        {onSendAdjustment && (
          <div className="border-t border-border px-3 py-2.5 shrink-0 bg-muted/30">
            {sent ? (
              <div className="flex items-center gap-1.5 text-xs font-medium text-green-600 py-1">
                <Check size={12} /> Sent to the agent
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submit() }}
                  placeholder={`Change something about the ${subject}…`}
                  className="flex-1 text-[13px] bg-background border border-border rounded-lg px-3 py-1.5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-blue-500/50"
                />
                {text.trim() && (
                  <button
                    onClick={submit}
                    className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors shrink-0"
                  >
                    <ArrowRight size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Label/value rows used by the brief and beat detail sheets. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70 mb-0.5">{label}</div>
      <div className="text-[13px] text-foreground leading-snug">{children}</div>
    </div>
  )
}

function PlayTileVisual({ isPlaying, onToggle }: { isPlaying: boolean; onToggle: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onToggle}
      className="absolute inset-0 flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors"
    >
      <span className={cn(
        'w-9 h-9 rounded-full flex items-center justify-center text-white transition-transform',
        isPlaying ? 'bg-blue-600 scale-110' : 'bg-blue-500',
      )}>
        {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </span>
    </button>
  )
}

/**
 * The greenlight board — a live view of every pre-production decision, shown
 * inline in chat right before generation burns credits. Reads project state
 * reactively (Convex), so later changes keep the latest board current.
 */
export function TreatmentBoard({
  workflowId,
  logline,
  coverUrl,
  notes,
  superseded,
  onConfirmSelection,
  existingAnswer,
}: {
  workflowId?: Id<'workflows'> | null
  logline: string
  coverUrl?: string
  notes?: string
  superseded?: boolean
  onConfirmSelection?: (message: string) => void
  existingAnswer?: string
}) {
  useCaptionPreviewFonts()
  const workflow = useQuery(api.workflows.get, workflowId ? { id: workflowId } : 'skip')
  const clips = useQuery(api.workflows.getClips, workflowId ? { workflowId } : 'skip')

  const [greenlit, setGreenlit] = useState(
    () => !!existingAnswer?.replace(/^​/, '').startsWith('Greenlight'),
  )
  const [expanded, setExpanded] = useState(false)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<{ src: string; alt: string } | null>(null)
  const [detail, setDetail] = useState<
    | { kind: 'brief' }
    | { kind: 'cast' | 'setting' | 'product' | 'element' | 'beat'; id: string }
    | { kind: 'palette' | 'voice' | 'music' | 'type' }
    | null
  >(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => () => { audioRef.current?.pause() }, [])

  // The chat autoscrolls to the bottom, which lands on the board's footer —
  // bring the cover into view once when the live board first mounts.
  const shouldScrollRef = useRef(!superseded && !greenlit && !existingAnswer)
  useEffect(() => {
    if (!shouldScrollRef.current) return
    shouldScrollRef.current = false
    const t = setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 450)
    return () => clearTimeout(t)
  }, [])

  const togglePlay = useCallback((id: string, url: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (playingId === id) {
      audioRef.current?.pause()
      setPlayingId(null)
    } else {
      audioRef.current?.pause()
      const audio = new Audio(url)
      audioRef.current = audio
      audio.onended = () => setPlayingId(null)
      audio.play()
      setPlayingId(id)
    }
  }, [playingId])

  const handleGreenlight = useCallback(() => {
    if (greenlit || !onConfirmSelection) return
    setGreenlit(true)
    onConfirmSelection(`​${GREENLIGHT_MESSAGE}`)
  }, [greenlit, onConfirmSelection])

  if (superseded && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors border border-border rounded-lg px-2.5 py-1.5"
      >
        <Film size={11} />
        Treatment board — superseded by a newer version
        <ChevronRight size={11} />
      </button>
    )
  }

  const brief = workflow?.brief
  const title = workflow?.title || 'Untitled'
  const aspect = workflow?.settings?.aspectRatio
  const castWithRefs = (workflow?.characters ?? []).filter(c => c.referenceImages?.length)
  const locationsWithRefs = (workflow?.locations ?? []).filter(l => l.referenceImages?.length)
  const productsWithRefs = (workflow?.products ?? []).filter(p => p.referenceImages?.length)
  const elementsWithRefs = (workflow?.elements ?? []).filter(el => el.referenceImages?.length)
  const voiceChar = (workflow?.characters ?? []).find(c => c.voiceId)
  const musicTrack = (workflow?.audioTracks ?? []).find(t => t.type === 'music')
  const captionKey = brief?.typography
    ? ['bold', 'youtube', 'tiktok_pop', 'hormozi', 'spotlight', 'colorful', 'cascade', 'neon', 'outline', 'solo', 'word_pop', 'word_slam', 'minimal', 'cinema', 'elegant', 'typewriter', 'broadcast']
        .find(k => brief.typography!.toLowerCase().includes(k.replace(/_/g, ' ')) || brief.typography!.toLowerCase().includes(k))
    : undefined
  const beats = [...(clips ?? [])].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  const totalDuration = beats.reduce((sum, c) => sum + (c.duration || 0), 0)
  const cover = coverUrl
    || locationsWithRefs[0]?.referenceImages?.[0]?.url
    || castWithRefs[0]?.referenceImages?.[0]?.url

  const formatChip = brief?.format || (aspect === '9:16' ? 'Vertical' : aspect === '1:1' ? 'Square' : undefined)
  const voicePreview = voiceChar?.voiceId ? getPresetVoicePreviewUrl(voiceChar.voiceId) : null

  return (
    <div ref={rootRef} className="scroll-mt-4 rounded-xl border border-border overflow-hidden bg-card shadow-sm">
      {/* Cover */}
      <div className="relative h-44 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-900">
        {cover && (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
        <div className="absolute top-3 left-3.5">
          <span className="px-1.5 py-0.5 rounded bg-white/15 backdrop-blur-sm text-white/90 text-[9px] font-semibold uppercase tracking-[0.14em]">
            Treatment
          </span>
        </div>
        <div className="absolute top-3 right-3 flex gap-1.5">
          {aspect && (
            <span className="px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white/90 text-[10px] font-medium">{aspect}</span>
          )}
          {totalDuration > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white/90 text-[10px] font-medium">{formatDuration(totalDuration)}</span>
          )}
          {formatChip && (
            <span className="px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white/90 text-[10px] font-medium max-w-[140px] truncate">{formatChip}</span>
          )}
        </div>
        <div className="absolute left-3.5 right-3.5 bottom-3">
          <h3 className="text-white text-[17px] font-semibold leading-tight truncate">{title}</h3>
          <p className="text-white/85 text-[12.5px] leading-snug line-clamp-2 mt-0.5">{logline}</p>
        </div>
      </div>

      {/* Decision tiles — each opens a detail sheet with the full data and an
          adjustment box that messages the agent */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 p-3">
        {brief && Object.keys(brief).length > 0 && (
          <BoardTile label="Brief" title={brief.objective?.split(/[,.—]/)[0].trim() || 'The plan'} onOpen={() => setDetail({ kind: 'brief' })}>
            <div className="absolute inset-0 p-2 flex flex-col gap-1 justify-center bg-muted">
              {[brief.objective, brief.tone, brief.avoid ? `Not: ${brief.avoid}` : undefined]
                .filter(Boolean).slice(0, 3).map((line, i) => (
                  <div key={i} className="text-[7.5px] leading-tight text-muted-foreground truncate border-l-2 border-blue-500/40 pl-1">
                    {line}
                  </div>
                ))}
              <div className="text-[7.5px] leading-tight text-blue-500/80 pl-1 font-medium">View all →</div>
            </div>
          </BoardTile>
        )}
        {castWithRefs.slice(0, 2).map(c => (
          <BoardTile key={c.id} label="Cast" title={c.name} onOpen={() => setDetail({ kind: 'cast', id: c.id })}>
            <img src={c.referenceImages![0].url} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
          </BoardTile>
        ))}
        {locationsWithRefs.slice(0, 2).map(l => (
          <BoardTile key={l.id} label="Setting" title={l.name} onOpen={() => setDetail({ kind: 'setting', id: l.id })}>
            <img src={l.referenceImages![0].url} alt={l.name} className="w-full h-full object-cover" loading="lazy" />
          </BoardTile>
        ))}
        {productsWithRefs.slice(0, 2).map(p => (
          <BoardTile key={p.id} label="Product" title={p.name} onOpen={() => setDetail({ kind: 'product', id: p.id })}>
            <img src={p.referenceImages![0].url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
          </BoardTile>
        ))}
        {elementsWithRefs.slice(0, 2).map(el => (
          <BoardTile key={el.id} label={el.kind || 'Element'} title={el.name} onOpen={() => setDetail({ kind: 'element', id: el.id })}>
            <img src={el.referenceImages![0].url} alt={el.name} className="w-full h-full object-cover" loading="lazy" />
          </BoardTile>
        ))}
        {!!brief?.palette?.length && (
          <BoardTile label="Palette" title={brief.look ? brief.look.split(/[,.—]/)[0].trim() : 'Color grade'} onOpen={() => setDetail({ kind: 'palette' })}>
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
              {brief.palette.slice(0, 4).map((c, i) => (
                <div key={i} style={{ backgroundColor: c }} />
              ))}
            </div>
          </BoardTile>
        )}
        {voiceChar?.voiceId && (
          <BoardTile label="Voice" title={getVoiceName(voiceChar.voiceId)} onOpen={() => setDetail({ kind: 'voice' })}>
            {voicePreview ? (
              <PlayTileVisual isPlaying={playingId === 'voice'} onToggle={(e) => togglePlay('voice', voicePreview, e)} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/50"><Music size={18} /></div>
            )}
          </BoardTile>
        )}
        {musicTrack && (
          <BoardTile label="Music" title={musicTrack.name} onOpen={() => setDetail({ kind: 'music' })}>
            <PlayTileVisual isPlaying={playingId === 'music'} onToggle={(e) => togglePlay('music', musicTrack.url, e)} />
          </BoardTile>
        )}
        {(captionKey || brief?.typography) && (
          <BoardTile label="Type" title={brief?.typography?.split(/[,.—]/)[0].trim() || 'Captions'} onOpen={() => setDetail({ kind: 'type' })}>
            <div className="absolute inset-0 bg-[#15171c] flex items-center justify-center p-1.5 overflow-hidden">
              {captionKey
                ? <CaptionSampleBox presetName={captionKey} fontSize={11} words={['Like', 'this']} />
                : <span className="text-white/80 text-[10px] text-center leading-snug px-1">{brief?.typography}</span>}
            </div>
          </BoardTile>
        )}
      </div>

      {/* Beats */}
      {beats.length > 0 && (
        <div className="px-3 pb-2">
          <div className="text-[8px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70 mb-1.5">
            The board — {beats.length} beat{beats.length !== 1 ? 's' : ''}
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1.5 snap-x">
            {beats.map((clip, i) => {
              const thumb = clip.firstFrameUrl || clip.storyboardImageUrl
              return (
                <div
                  key={clip._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetail({ kind: 'beat', id: clip._id })}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ kind: 'beat', id: clip._id }) } }}
                  className="flex-shrink-0 w-[92px] snap-start rounded-md border border-border overflow-hidden cursor-pointer transition-all hover:border-blue-500/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                >
                  <div className="h-[52px] bg-muted relative">
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground/40 text-[11px] font-semibold">
                        {i + 1}
                      </div>
                    )}
                  </div>
                  <div className="px-1.5 py-1">
                    <div className="text-[9.5px] font-medium text-foreground truncate leading-tight">{clip.title || `Clip ${i + 1}`}</div>
                    <div className="text-[8.5px] text-muted-foreground">{clip.duration}s</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Notes from the director */}
      {notes && (
        <div className="mx-3 mb-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25 px-2.5 py-1.5 text-[11.5px] text-foreground/85 leading-snug">
          {notes}
        </div>
      )}

      {/* Footer — the gate */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-border bg-muted/30">
        <span className="text-[11px] text-muted-foreground shrink-0">
          {beats.length > 0 && `${beats.length} clip${beats.length !== 1 ? 's' : ''}`}
          {totalDuration > 0 && ` · ${formatDuration(totalDuration)}`}
        </span>
        {superseded ? (
          <span className="text-[11px] text-muted-foreground">Superseded by a newer board</span>
        ) : greenlit ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-green-600">
            <Check size={13} /> Greenlit — generating
          </span>
        ) : (
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-[11px] text-muted-foreground hidden sm:block truncate">click any tile to adjust</span>
            <button
              onClick={handleGreenlight}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-[13px] font-semibold transition-colors shadow-sm shrink-0"
            >
              <Sparkles size={13} />
              Greenlight — make the video
            </button>
          </div>
        )}
      </div>

      {/* Detail sheets */}
      {detail?.kind === 'brief' && brief && (
        <BoardDetailSheet title="Creative brief" subject="brief" onClose={() => setDetail(null)} onSendAdjustment={onConfirmSelection}>
          {brief.logline && <DetailRow label="Logline">{brief.logline}</DetailRow>}
          {brief.objective && <DetailRow label="Objective">{brief.objective}</DetailRow>}
          {brief.audience && <DetailRow label="Audience">{brief.audience}</DetailRow>}
          {brief.format && <DetailRow label="Format">{brief.format}</DetailRow>}
          {brief.tone && <DetailRow label="Tone">{brief.tone}</DetailRow>}
          {brief.look && <DetailRow label="Look">{brief.look}</DetailRow>}
          {!!brief.palette?.length && (
            <DetailRow label="Palette">
              <span className="inline-flex gap-1 align-middle">
                {brief.palette.map((c, i) => (
                  <span key={i} className="w-5 h-5 rounded border border-border/50 inline-block" style={{ backgroundColor: c }} title={c} />
                ))}
              </span>
            </DetailRow>
          )}
          {brief.world && <DetailRow label="World">{brief.world}</DetailRow>}
          {brief.product && <DetailRow label="Product">{brief.product}</DetailRow>}
          {brief.sound && <DetailRow label="Sound">{brief.sound}</DetailRow>}
          {brief.typography && <DetailRow label="Type">{brief.typography}</DetailRow>}
          {brief.hook && <DetailRow label="Hook">{brief.hook}</DetailRow>}
          {brief.ending && <DetailRow label="Ending">{brief.ending}</DetailRow>}
          {brief.references && <DetailRow label="References">{brief.references}</DetailRow>}
          {brief.avoid && <DetailRow label="Avoid">{brief.avoid}</DetailRow>}
          {(brief.custom ?? []).map(c => (
            <DetailRow key={c.id} label={c.label}>{c.value}</DetailRow>
          ))}
        </BoardDetailSheet>
      )}
      {detail?.kind === 'cast' && (() => {
        const c = (workflow?.characters ?? []).find(ch => ch.id === detail.id)
        if (!c) return null
        const preview = c.voiceId ? getPresetVoicePreviewUrl(c.voiceId) : null
        return (
          <BoardDetailSheet title={c.name} subject={`cast (${c.name})`} onClose={() => setDetail(null)} onSendAdjustment={onConfirmSelection}>
            {!!c.referenceImages?.length && (
              <div className="flex gap-2">
                {c.referenceImages.map((img, i) => (
                  <img
                    key={i}
                    src={img.url}
                    alt={c.name}
                    className="w-1/2 min-w-0 rounded-lg border border-border object-cover cursor-zoom-in"
                    onClick={() => setLightboxUrl({ src: img.url, alt: c.name })}
                  />
                ))}
              </div>
            )}
            <DetailRow label="Description">{c.description}</DetailRow>
            {c.voiceId && (
              <DetailRow label="Voice">
                <span className="inline-flex items-center gap-2">
                  {getVoiceName(c.voiceId)}
                  {preview && (
                    <button
                      onClick={(e) => togglePlay(`sheet-voice-${c.id}`, preview, e)}
                      className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center"
                    >
                      {playingId === `sheet-voice-${c.id}` ? <Pause size={10} /> : <Play size={10} className="ml-0.5" />}
                    </button>
                  )}
                </span>
              </DetailRow>
            )}
          </BoardDetailSheet>
        )
      })()}
      {(detail?.kind === 'setting' || detail?.kind === 'product' || detail?.kind === 'element') && (() => {
        const list = detail.kind === 'setting' ? workflow?.locations
          : detail.kind === 'product' ? workflow?.products
          : workflow?.elements
        const ent = (list ?? []).find(e => e.id === detail.id)
        if (!ent) return null
        const subjectNoun = detail.kind === 'element' && 'kind' in ent && ent.kind ? ent.kind : detail.kind
        return (
          <BoardDetailSheet
            title={ent.name}
            subject={`${subjectNoun} (${ent.name})`}
            onClose={() => setDetail(null)}
            onSendAdjustment={onConfirmSelection}
          >
            {!!ent.referenceImages?.length && (
              <img
                src={ent.referenceImages[0].url}
                alt={ent.name}
                className="w-full rounded-lg border border-border object-cover cursor-zoom-in"
                onClick={() => setLightboxUrl({ src: ent.referenceImages![0].url, alt: ent.name })}
              />
            )}
            <DetailRow label="Description">{ent.description}</DetailRow>
          </BoardDetailSheet>
        )
      })()}
      {detail?.kind === 'palette' && brief && (
        <BoardDetailSheet title="Palette & look" subject="palette" onClose={() => setDetail(null)} onSendAdjustment={onConfirmSelection}>
          {!!brief.palette?.length && (
            <div className="flex gap-1.5">
              {brief.palette.map((c, i) => (
                <div key={i} className="flex-1 min-w-0">
                  <div className="h-14 rounded-lg border border-border/50" style={{ backgroundColor: c }} />
                  <div className="text-[9px] text-muted-foreground text-center mt-1 truncate">{c}</div>
                </div>
              ))}
            </div>
          )}
          {brief.look && <DetailRow label="Look">{brief.look}</DetailRow>}
        </BoardDetailSheet>
      )}
      {detail?.kind === 'voice' && voiceChar?.voiceId && (
        <BoardDetailSheet title={`Voice — ${getVoiceName(voiceChar.voiceId)}`} subject="voice" onClose={() => setDetail(null)} onSendAdjustment={onConfirmSelection}>
          <div className="flex items-center gap-3">
            {voicePreview && (
              <button
                onClick={(e) => togglePlay('sheet-voice', voicePreview, e)}
                className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0"
              >
                {playingId === 'sheet-voice' ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
              </button>
            )}
            <div className="text-[13px] text-foreground">
              {getVoiceName(voiceChar.voiceId)} — narrating as {voiceChar.name}
            </div>
          </div>
          {brief?.sound && <DetailRow label="Sound direction">{brief.sound}</DetailRow>}
        </BoardDetailSheet>
      )}
      {detail?.kind === 'music' && musicTrack && (
        <BoardDetailSheet title={`Music — ${musicTrack.name}`} subject="music" onClose={() => setDetail(null)} onSendAdjustment={onConfirmSelection}>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => togglePlay('sheet-music', musicTrack.url, e)}
              className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0"
            >
              {playingId === 'sheet-music' ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
            </button>
            <div className="text-[13px] text-foreground min-w-0">
              <div className="font-medium truncate">{musicTrack.name}</div>
              <div className="text-[11px] text-muted-foreground">Volume {Math.round((musicTrack.volume ?? 1) * 100)}%</div>
            </div>
          </div>
        </BoardDetailSheet>
      )}
      {detail?.kind === 'type' && (
        <BoardDetailSheet title="Caption style" subject="caption style" onClose={() => setDetail(null)} onSendAdjustment={onConfirmSelection}>
          <div className="rounded-lg bg-[#15171c] flex items-center justify-center py-8">
            {captionKey
              ? <CaptionSampleBox presetName={captionKey} fontSize={20} words={['Captions', 'like this']} />
              : <span className="text-white/85 text-sm px-4 text-center">{brief?.typography}</span>}
          </div>
          {brief?.typography && <DetailRow label="Direction">{brief.typography}</DetailRow>}
        </BoardDetailSheet>
      )}
      {detail?.kind === 'beat' && (() => {
        const idx = beats.findIndex(b => b._id === detail.id)
        const clip = beats[idx]
        if (!clip) return null
        const thumb = clip.firstFrameUrl || clip.storyboardImageUrl
        const clipTitle = clip.title || `Clip ${idx + 1}`
        return (
          <BoardDetailSheet
            title={`Beat ${idx + 1} — ${clipTitle}`}
            subject={`"${clipTitle}" clip`}
            onClose={() => setDetail(null)}
            onSendAdjustment={onConfirmSelection}
          >
            {thumb && (
              <img
                src={thumb}
                alt={clipTitle}
                className="w-full rounded-lg border border-border object-cover cursor-zoom-in"
                onClick={() => setLightboxUrl({ src: thumb, alt: clipTitle })}
              />
            )}
            <DetailRow label="Duration">{clip.duration}s</DetailRow>
            {clip.description && <DetailRow label="Description">{clip.description}</DetailRow>}
            {!!clip.dialog?.length && (
              <DetailRow label="Dialog">
                {clip.dialog.map((d, i) => (
                  <div key={i}><span className="font-medium">{d.characterName}:</span> “{d.text}”</div>
                ))}
              </DetailRow>
            )}
            {clip.videoPrompt && (
              <DetailRow label="Shot direction">
                <span className="text-[12px] text-muted-foreground leading-snug">{clip.videoPrompt}</span>
              </DetailRow>
            )}
          </BoardDetailSheet>
        )
      })()}

      {lightboxUrl && <ImageLightbox src={lightboxUrl.src} alt={lightboxUrl.alt} onClose={() => setLightboxUrl(null)} />}
    </div>
  )
}

// --- Dispatcher: renders the right widget based on tool result type ---

export function VisualToolWidget({
  data,
  layout,
  onSendSelection,
  onSendFiles,
  existingAnswer,
  workflowId,
  superseded,
  widgetKey,
}: {
  data: Record<string, unknown>
  layout?: 'horizontal' | 'vertical'
  onSendSelection?: (message: string) => void
  onSendFiles?: (message: string, files: File[]) => void
  existingAnswer?: string
  workflowId?: Id<'workflows'> | null
  superseded?: boolean
  /** Stable identity (tool-call id) — lets widgets keep progress across remounts */
  widgetKey?: string
}) {
  const type = data?.type as string

  switch (type) {
    case 'question_options':
      return (
        <QuestionOptions
          question={(data.question as string) || ''}
          category={data.category as string | undefined}
          options={(() => {
            const raw = data.options
            if (Array.isArray(raw)) return raw as QuestionOption[]
            if (typeof raw === 'string') try { return JSON.parse(raw) as QuestionOption[] } catch { return [] }
            return []
          })()}
          selectionMode={(data.selection_mode as 'single' | 'multi') || 'single'}
          onConfirmSelection={onSendSelection}
          existingAnswer={existingAnswer}
        />
      )
    case 'treatment_board':
      return (
        <TreatmentBoard
          workflowId={workflowId}
          logline={(data.logline as string) || ''}
          coverUrl={data.cover_url as string | undefined}
          notes={data.notes as string | undefined}
          superseded={superseded}
          onConfirmSelection={onSendSelection}
          existingAnswer={existingAnswer}
        />
      )
    case 'asset_request':
      return (
        <AssetRequestCard
          requests={(() => {
            const raw = data.requests
            if (Array.isArray(raw)) return raw as AssetRequestItem[]
            if (typeof raw === 'string') try { return JSON.parse(raw) as AssetRequestItem[] } catch { return [] }
            return []
          })()}
          note={data.note as string | undefined}
          onSendSelection={onSendSelection}
          onSendFiles={onSendFiles}
          cacheKey={widgetKey}
        />
      )
    case 'question_sequence':
      return (
        <QuestionSequence
          questions={(() => {
            const raw = data.questions
            if (Array.isArray(raw)) return raw as SequenceQuestion[]
            if (typeof raw === 'string') try { return JSON.parse(raw) as SequenceQuestion[] } catch { return [] }
            return []
          })()}
          onConfirmSelection={onSendSelection}
          existingAnswer={existingAnswer}
          cacheKey={widgetKey}
        />
      )
    case 'media_grid':
      return (
        <MediaGrid
          items={(data.items as MediaItem[]) || []}
          selectionMode={(data.selection_mode as 'single' | 'multi' | 'none') || 'multi'}
          label={data.label as string | undefined}
          onConfirmSelection={onSendSelection}
        />
      )
    case 'media_clusters':
      return (
        <ClusterCards
          clusters={(data.clusters as MediaCluster[]) || []}
          layout={layout}
          label={data.label as string | undefined}
          onConfirmSelection={onSendSelection}
        />
      )
    case 'folder_preview':
      return (
        <FolderPreview
          rootPath={(data.root_path as string) || ''}
          tree={(data.tree as FolderEntry[]) || []}
          label={data.label as string | undefined}
        />
      )
    default:
      return null
  }
}
